import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type { WorkItemPathEntry } from '@localagent/sdk';
import { errorWithNothingCachedToShow } from '../../lib/query-error';
import { getSDK } from '../../services/sdk-instance';
import { holdsIdentityAtom } from '../../stores/auth/atoms';
import { sdkConfiguredAtom } from '../../stores/sdk/atoms';

/**
 * What produced this file: the work items that wrote it, newest first
 * (work_management ADR-028). More than one entry is ordinary — a
 * draft written by one work item and revised by another is two entries.
 */

const KIND_LABELS: Record<WorkItemPathEntry['work_item_kind'], string> = {
  goal: 'Goal',
  project: 'Project',
  task: 'Task',
};

export function FileWorkItemTrail({
  workspaceId,
  path,
}: {
  workspaceId: string;
  /** Workspace-relative path of the file in view; null while none is open. */
  path: string | null;
}) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  const {
    data: trail,
    error,
    isLoading,
  } = useQuery<WorkItemPathEntry[], Error>({
    queryKey: ['work', 'file-work-items', workspaceId, path],
    queryFn: () => getSDK().workManagement.listPathWorkItems(workspaceId, { path: path as string }),
    enabled: Boolean(isAuthenticated && sdkConfigured && path),
  });
  const trailError = errorWithNothingCachedToShow(trail, error);

  if (!path) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-1 text-[10px]"
      data-testid="file-work-item-trail"
      data-path={path}
      data-count={trail?.length ?? 0}
    >
      <span className="text-muted-foreground">Produced by</span>

      {isLoading && <span className="text-muted-foreground">loading...</span>}
      {trailError && (
        <span className="text-destructive" role="alert" data-testid="file-work-item-trail-error">
          could not be loaded: {trailError.message}
        </span>
      )}
      {trail && trail.length === 0 && (
        <span className="text-muted-foreground">no work item on record</span>
      )}

      {trail?.map((entry) => (
        <Link
          key={`${entry.work_item_kind}:${entry.work_item_id}`}
          to={`/chat/${entry.session_id}`}
          title={`Open the session that wrote this file for ${KIND_LABELS[
            entry.work_item_kind
          ].toLowerCase()} ${entry.identifier ?? entry.work_item_id}`}
          data-testid={`file-work-item-${entry.work_item_id}`}
          data-work-item-kind={entry.work_item_kind}
          className="inline-flex max-w-[16rem] items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 font-medium text-primary transition-colors hover:bg-primary/20"
        >
          <span className="uppercase tracking-wide opacity-70">
            {KIND_LABELS[entry.work_item_kind]}
          </span>
          <span className="truncate font-mono">{entry.identifier ?? entry.work_item_id}</span>
          {entry.title && <span className="truncate opacity-80">{entry.title}</span>}
        </Link>
      ))}
    </div>
  );
}
